import hash from "object-hash";
import React from "react";
import { LiveComponentSourceProps } from ".";
import { loadRemoteComponent } from "./federation";
import { RemoteComponentInfo } from "./RemoteComponent";

/**
 * In order to prevent state from getting wiped out we store a reference to each
 * LazyLiveComponent outside of the react context.
 *
 * https://react.dev/reference/react/lazy#troubleshooting
 */
export type LazyLiveComponent = React.LazyExoticComponent<React.ComponentType<LiveComponentSourceProps>>;
const store: {
  lazyComponents: Record<string, LazyLiveComponent>;
} = {
  lazyComponents: {},
};

/**
 * Wipes out the global store. Enables testing on a global object.
 */
const reset = () => {
  store.lazyComponents = {};
};

/**
 * Each combination of props will be "cached" as it's own component.
 *
 * This grows unbounded. At some point this may cause an issue and some kind
 * of garbage collecting would need to be implemented. That's likely quite a ways out until we hit
 * that bottle-neck but it's something to keep an eye out for.
 */
const populateAndGet = (info: RemoteComponentInfo) => {
  const key = hash(info);
  store.lazyComponents[key] ??= React.lazy(loadRemoteComponent(info));
  return store.lazyComponents[key];
};

export const LazyRemoteComponentStore = {
  populateAndGet,
  reset,
  length: () => Object.keys(store.lazyComponents).length,
};
